import { getCookie } from 'cookies-next';
import type { NextRouter } from 'next/router';
import { WEB_VITALS_TARGETS } from './webVitals';
import { watchProfileReadiness } from './profileReadiness';

/**
 * RUM for client-side route transitions (tap → new page on screen).
 *
 * Core Web Vitals only describe the first load; after that, every navigation is a pages
 * router transition that blocks on `/_next/data` and then renders. This measures each
 * transition split into phases so we can tell *network* from *render* on real phones —
 * the question docs/performance.md is built around.
 *
 * Privacy: only route *templates* (`/dr/[slug]`) are sent — never the slug, path or query
 * string (which doctor/specialty a patient looked at is health-adjacent data).
 */

/** Share of page loads that report. Decided once per page load, not per transition. */
export const ROUTE_TRANSITION_SAMPLE_RATE = 0.25;

/** Same HEC + dedicated `p24_web_vitals` index as web vitals, separate sourcetype. */
const TARGET = WEB_VITALS_TARGETS[0];

export type DataSource = 'network' | 'cache' | 'none';

export interface ResourceSample {
  name: string;
  startTime: number;
  responseEnd: number;
  transferSize: number;
  decodedBodySize: number;
}

export interface RouteTransitionPayload {
  event_group: 'route_transition';
  event_type: 'route_transition';
  from_route: string;
  to_route: string;
  shallow: boolean;
  /** routeChangeStart → routeChangeComplete (Next has rendered the new page) */
  total_ms: number;
  /** routeChangeStart → next frame after complete (≈ first paint of new page) */
  paint_ms: number | null;
  /** routeChangeStart → `/_next/data` response end; null if none was fetched */
  data_ms: number | null;
  data_source: DataSource;
  /** routeChangeStart → last JS chunk loaded during the transition; null if none */
  chunks_ms: number | null;
  chunks_count: number;
  /** total minus the network phase: JS execution + React render/commit */
  render_ms: number;
  build_id: string | null;
  device: 'mobile' | 'desktop';
  connection: string | null;
  is_application: boolean;
  terminal_id: unknown;
  transition_id: string;
}

const round = (n: number) => Math.round(n);

/** `/s/tehran/` → `/s/tehran`, `/` → `` — the part of a data URL after build id + locale. */
export function dataPathOf(pathname: string): string {
  const clean = pathname.split(/[?#]/)[0].replace(/\/+$/, '');
  try {
    return decodeURIComponent(clean);
  } catch {
    return clean;
  }
}

/** `https://x/_next/data/<build>/fa/s/tehran.json?params=…` → `/s/tehran` */
export function dataPathFromEntry(url: string): string | undefined {
  const m = url.match(/\/_next\/data\/[^/]+(?:\/[a-z]{2})?((?:\/[^?]*?)?)\.json(?:\?|$)/);
  if (!m) return undefined;
  return dataPathOf(m[1] === '/index' ? '' : m[1]);
}

/** Pure — splits one transition into phases from the resource entries seen during it. */
export function summarizeTransition(input: {
  startedAt: number;
  completedAt: number;
  /** pathname of the navigation target (routeChangeStart url) */
  targetPath?: string;
  paintedAt: number | null;
  resources: ResourceSample[];
}) {
  const { startedAt, completedAt, paintedAt } = input;
  const during = input.resources.filter(r => r.startTime >= startedAt && r.startTime <= completedAt);

  const dataEntries = during.filter(r => r.name.includes('/_next/data/'));
  // Other data requests can run inside the window (e.g. intent prefetch of another link);
  // only the destination's own request counts. Unknown target → old behavior (last one).
  const target = input.targetPath === undefined ? undefined : dataPathOf(input.targetPath);
  const matching = target === undefined ? dataEntries : dataEntries.filter(r => dataPathFromEntry(r.name) === target);
  const data = matching[matching.length - 1];
  // Same-origin, so sizes are exposed: 0 bytes over the wire with a body = HTTP cache hit.
  const dataSource: DataSource = !data ? 'none' : data.transferSize === 0 && data.decodedBodySize > 0 ? 'cache' : 'network';

  const chunks = during.filter(r => r.name.includes('/_next/static/'));
  const dataEnd = data ? data.responseEnd - startedAt : 0;
  const chunksEnd = chunks.length ? Math.max(...chunks.map(c => c.responseEnd)) - startedAt : 0;
  const total = completedAt - startedAt;

  return {
    total_ms: round(total),
    paint_ms: paintedAt === null ? null : round(paintedAt - startedAt),
    data_ms: data ? round(dataEnd) : null,
    data_source: dataSource,
    chunks_ms: chunks.length ? round(chunksEnd) : null,
    chunks_count: chunks.length,
    render_ms: round(Math.max(0, total - Math.max(dataEnd, chunksEnd))),
  };
}

type RouterLike = Pick<NextRouter, 'pathname' | 'events'>;

/** Debug/QA: `localStorage.setItem('p24_rum_route_transition', '1')` bypasses sampling. */
export const FORCE_SAMPLE_KEY = 'p24_rum_route_transition';

const PAINT_TIMEOUT_MS = 500;

function isForcedSample() {
  try {
    return window.localStorage.getItem(FORCE_SAMPLE_KEY) === '1';
  } catch {
    return false;
  }
}

function send(payload: RouteTransitionPayload) {
  void fetch(`${TARGET.baseUrl}/services/collector/event`, {
    method: 'POST',
    headers: { Authorization: `Splunk ${TARGET.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ event: payload, sourcetype: 'route_transition' }),
    keepalive: true,
    mode: 'cors',
    credentials: 'omit',
  }).catch(() => undefined);
}

/**
 * Starts listening. Returns a cleanup function. Monitoring must never hurt the user:
 * every failure is swallowed, and reporting is deferred until the new page has painted.
 */
export function startRouteTransitionRum(router: RouterLike): () => void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return () => undefined;
  if (Math.random() >= ROUTE_TRANSITION_SAMPLE_RATE && !isForcedSample()) return () => undefined;

  // An observer (not getEntriesByType) because the 250-entry resource buffer fills up on
  // long sessions and then silently drops exactly the entries we need.
  const resources: ResourceSample[] = [];
  let observer: PerformanceObserver | undefined;
  try {
    observer = new PerformanceObserver(list => {
      for (const e of list.getEntries() as PerformanceResourceTiming[]) {
        if (!e.name.includes('/_next/')) continue;
        resources.push({
          name: e.name,
          startTime: e.startTime,
          responseEnd: e.responseEnd,
          transferSize: e.transferSize,
          decodedBodySize: e.decodedBodySize,
        });
      }
      if (resources.length > 200) resources.splice(0, resources.length - 200);
    });
    observer.observe({ type: 'resource', buffered: false });
  } catch {
    return () => undefined;
  }

  let sequence = 0;
  const sessionId = Math.random().toString(36).slice(2);
  let current: { startedAt: number; from: string; shallow: boolean; targetPath: string; id: string } | null = null;
  let readiness: ReturnType<typeof watchProfileReadiness> | undefined;
  let readinessPath = '';

  const onStart = (url: string, opts?: { shallow?: boolean }) => {
    readiness?.(); readiness = undefined;
    current = { startedAt: performance.now(), from: router.pathname, shallow: !!opts?.shallow, targetPath: url, id: `${sessionId}-${++sequence}` };
    const match = /^\/dr\/([^/?#]+)\/?(?:[?#]|$)/.exec(url);
    if (match && !opts?.shallow) {
      try {
        const started = current;
        readinessPath = dataPathOf(url);
        readiness = watchProfileReadiness({ slug: decodeURIComponent(match[1]), startedAt: started.startedAt, transitionId: started.id, fromRoute: started.from }, metric => {
          void fetch(`${TARGET.baseUrl}/services/collector/event`, {
            method: 'POST', headers: { Authorization: `Splunk ${TARGET.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ sourcetype: 'profile_readiness', event: {
              event_group: 'profile_readiness', event_type: 'profile_readiness', ...metric,
              build_id: (window as any).__NEXT_DATA__?.buildId ?? null,
              device: window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop',
              connection: (navigator as any)?.connection?.effectiveType ?? null,
              is_application: window.matchMedia('(display-mode: standalone)').matches,
            } }), keepalive: true, mode: 'cors', credentials: 'omit',
          }).catch(() => undefined);
        });
      } catch { /* monitoring must never break navigation */ }
    }
  };
  const onError = (_error: unknown, url?: string) => {
    if (url && current && dataPathOf(url) !== dataPathOf(current.targetPath)) return;
    if (!url || dataPathOf(url) === readinessPath) { readiness?.(); readiness = undefined; }
    current = null; // cancelled / superseded navigation — not a real transition
  };
  const onComplete = (url?: string) => {
    const started = current;
    if (!started) return;
    if (url && dataPathOf(url) !== dataPathOf(started.targetPath)) return;
    current = null;
    readiness?.routeComplete();
    const completedAt = performance.now();
    const to = router.pathname;
    // rAF never fires in a hidden tab (user switched away right after tapping), so don't
    // hang on it: after PAINT_TIMEOUT_MS report with paint_ms = null instead of losing it.
    let reported = false;
    const report = (paintedAt: number | null) => {
      if (reported) return;
      reported = true;
      // Give the observer time to deliver the entries of this transition.
      setTimeout(() => {
        try {
          const phases = summarizeTransition({
            startedAt: started.startedAt,
            completedAt,
            targetPath: started.targetPath,
            paintedAt,
            resources,
          });
          send({
            event_group: 'route_transition',
            event_type: 'route_transition',
            from_route: started.from,
            to_route: to,
            shallow: started.shallow,
            ...phases,
            build_id: (window as any).__NEXT_DATA__?.buildId ?? null,
            device: window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop',
            connection: (navigator as any)?.connection?.effectiveType ?? null,
            is_application: window.matchMedia('(display-mode: standalone)').matches,
            terminal_id: getCookie('terminal_id') ?? null,
            transition_id: started.id,
          });
        } catch {
          // silent
        }
      }, 1000);
    };
    requestAnimationFrame(() => report(performance.now()));
    setTimeout(() => report(null), PAINT_TIMEOUT_MS);
  };

  router.events.on('routeChangeStart', onStart);
  router.events.on('routeChangeComplete', onComplete);
  router.events.on('routeChangeError', onError);
  return () => {
    router.events.off('routeChangeStart', onStart);
    router.events.off('routeChangeComplete', onComplete);
    router.events.off('routeChangeError', onError);
    observer?.disconnect();
    readiness?.();
  };
}
